import axios from 'axios';

// Configuration des clés API
// Note: En production, utilisez des variables d'environnement pour sécuriser les clés
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || 'AIzaSyCY7uOBvLt19x-es3pWukTIy2WouVcP-Ww';
const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY || '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';

// Désactiver temporairement l'IA si les modèles ne fonctionnent pas
// Mettre à true quand les modèles Gemini/Groq fonctionnent correctement
const AI_ENABLED = false;

interface AIResponse {
  text: string;
  source: 'gemini' | 'groq';
  error?: string;
}

// Prompt système pour le contexte islamique
const ISLAMIC_SYSTEM_PROMPT = `Tu es un assistant islamique intelligent, bienveillant et érudit. Ton rôle est d'aider les utilisateurs avec des questions sur l'Islam.

Règles importantes:
1. **Langue:** Réponds principalement en français, mais utilise l'arabe pour les termes religieux importants (Salat, Zakat, Hajj, etc.)
2. **Précision:** Base-toi uniquement sur des sources authentiques (Coran, Hadiths Sahih, consensus des savants)
3. **Respect:** Sois toujours respectueux, bienveillant et encourageant
4. **Honnêteté:** Si tu ne connais pas une réponse précise, dis-le et oriente vers des sources fiables
5. **Contextualisation:** Quand tu cites un verset ou hadith, donne le contexte et la référence si possible
6. **Pratique:** Encourage les bonnes pratiques islamiques avec bienveillance
7. **Concision:** Sois concis mais complet - structure tes réponses avec des émojis et des sections claires
8. **Multiculturalisme:** Sois ouvert et respectueux de toutes les écoles de pensée islamiques (Hanafi, Maliki, Shafi'i, Hanbali)
9. **Orientation:** À la fin de ta réponse, suggère des sites islamiques fiables pour approfondir (SunnaSearch, IslamHouse, etc.)

Format de réponse:
- Utilise des émojis appropriés (🕌 📖 📜 🤲 etc.)
- Structure avec des titres en gras (**Titre**)
- Utilise des listes à puces pour la clarté
- Ajoute des références quand c'est pertinent (Coran, Hadith)
- Termine par une section "📚 Pour approfondir" avec des liens vers des sites fiables

Réponds de manière claire, structurée, bienveillante et érudite.`;

class AIService {
  // Modèles Gemini disponibles (essayer dans l'ordre si l'un ne fonctionne pas)
  // Format : v1beta/models/{MODEL_NAME}:generateContent
  // Modèles possibles : gemini-pro, gemini-1.5-pro, gemini-1.5-flash
  // Pour l'instant, désactivé car les modèles retournent 404
  private geminiUrl = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent';
  private groqUrl = 'https://api.groq.com/openai/v1/chat/completions';

  // Générer une réponse avec Gemini (avec contexte conversationnel)
  private async generateWithGemini(
    prompt: string, 
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>
  ): Promise<string> {
    try {
      // Construire l'historique de conversation pour Gemini
      // Gemini utilise un format différent : pas de système, juste user/model en alternance
      const contents: any[] = [];
      
      // Ajouter le prompt système et l'historique dans le premier message
      let fullPrompt = `${ISLAMIC_SYSTEM_PROMPT}\n\n`;
      
      // Ajouter l'historique de conversation (limité aux 10 derniers messages)
      if (conversationHistory && conversationHistory.length > 0) {
        const recentHistory = conversationHistory.slice(-10); // Garder les 10 derniers messages
        for (const msg of recentHistory) {
          fullPrompt += `${msg.role === 'user' ? 'Utilisateur' : 'Assistant'}: ${msg.content}\n\n`;
        }
      }
      
      // Ajouter le message actuel
      fullPrompt += `Utilisateur: ${prompt}\n\nAssistant:`;
      
      // Gemini utilise un seul message avec tout le contexte
      contents.push({
        role: 'user',
        parts: [{ text: fullPrompt }],
      });
      
      const response = await axios.post(
        `${this.geminiUrl}?key=${GEMINI_API_KEY}`,
        {
          contents: contents,
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 1024,
          },
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 15000,
        }
      );

      if (response.data && response.data.candidates && response.data.candidates[0]) {
        const text = response.data.candidates[0].content.parts[0].text.trim();
        console.log('[Gemini] Réponse reçue avec succès:', text.substring(0, 100));
        return text;
      }

      console.error('[Gemini] Réponse invalide:', response.data);
      throw new Error('Réponse invalide de Gemini');
    } catch (error: any) {
      console.error('[Gemini] Erreur détaillée:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw new Error(`Erreur Gemini: ${error.response?.data?.error?.message || error.message || 'Service indisponible'}`);
    }
  }

  // Générer une réponse avec Groq (avec contexte conversationnel)
  private async generateWithGroq(
    prompt: string,
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>
  ): Promise<string> {
    try {
      // Construire les messages avec l'historique
      const messages: any[] = [
        {
          role: 'system',
          content: ISLAMIC_SYSTEM_PROMPT,
        },
      ];
      
      // Ajouter l'historique de conversation (limité aux 10 derniers messages)
      if (conversationHistory && conversationHistory.length > 0) {
        const recentHistory = conversationHistory.slice(-10); // Garder les 10 derniers messages
        for (const msg of recentHistory) {
          messages.push({
            role: msg.role === 'user' ? 'user' : 'assistant',
            content: msg.content,
          });
        }
      }
      
      // Ajouter le message actuel
      messages.push({
        role: 'user',
        content: prompt,
      });
      
      const response = await axios.post(
        this.groqUrl,
        {
          messages: messages,
          model: 'llama-3.1-70b-versatile',  // Modèle stable et disponible
          temperature: 0.7,
          max_tokens: 2048,
          top_p: 0.9,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${GROQ_API_KEY}`,
          },
          timeout: 15000,
        }
      );

      if (response.data && response.data.choices && response.data.choices[0]) {
        const text = response.data.choices[0].message.content.trim();
        console.log('[Groq] Réponse reçue avec succès:', text.substring(0, 100));
        return text;
      }

      console.error('[Groq] Réponse invalide:', response.data);
      throw new Error('Réponse invalide de Groq');
    } catch (error: any) {
      console.error('[Groq] Erreur détaillée:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw new Error(`Erreur Groq: ${error.response?.data?.error?.message || error.message || 'Service indisponible'}`);
    }
  }

  // Générer une réponse avec fallback automatique et contexte conversationnel
  async generateResponse(
    prompt: string, 
    preferFast: boolean = false,
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>
  ): Promise<AIResponse> {
    // Si l'IA est désactivée, retourner une réponse par défaut
    if (!AI_ENABLED) {
      return {
        text: 'Désolé, les services IA sont temporairement indisponibles. Veuillez utiliser les boutons rapides ou poser des questions spécifiques sur les exposants, événements, daaras ou marabouts disponibles dans notre base de données.',
        source: 'gemini',
        error: 'IA désactivée',
      };
    }
    
    // Si on préfère la vitesse, on essaie Groq d'abord
    if (preferFast) {
      try {
        const response = await this.generateWithGroq(prompt, conversationHistory);
        return {
          text: response,
          source: 'groq',
        };
      } catch (error) {
        // Fallback sur Gemini
        try {
          const response = await this.generateWithGemini(prompt, conversationHistory);
          return {
            text: response,
            source: 'gemini',
          };
        } catch (geminiError) {
          return {
            text: 'Désolé, les services IA sont temporairement indisponibles. Veuillez réessayer plus tard.',
            source: 'groq',
            error: 'Tous les services IA sont indisponibles',
          };
        }
      }
    }

    // Sinon, on essaie Gemini d'abord (meilleure qualité)
    try {
      const response = await this.generateWithGemini(prompt, conversationHistory);
      return {
        text: response,
        source: 'gemini',
      };
    } catch (error) {
      // Fallback sur Groq
      try {
        const response = await this.generateWithGroq(prompt, conversationHistory);
        return {
          text: response,
          source: 'groq',
        };
      } catch (groqError) {
        return {
          text: 'Désolé, les services IA sont temporairement indisponibles. Veuillez réessayer plus tard.',
          source: 'gemini',
          error: 'Tous les services IA sont indisponibles',
        };
      }
    }
  }

  // Vérifier si une question nécessite l'IA (questions ouvertes, explications)
  shouldUseAI(message: string): boolean {
    const lowerMessage = message.toLowerCase();
    
    // Mots-clés qui indiquent une question ouverte
    const openQuestionKeywords = [
      'pourquoi', 'comment', 'explique', 'expliquer', 'qu\'est-ce que', 'que signifie',
      'quelle est la différence', 'peux-tu m\'aider', 'aide-moi', 'conseille',
      'quoi faire', 'que faire', 'donne-moi des conseils', 'parle-moi de',
      'why', 'how', 'explain', 'what is', 'tell me about', 'help me',
      'لماذا', 'كيف', 'اشرح', 'ما هو', 'ما معنى'
    ];

    // Si le message contient un mot-clé de question ouverte
    if (openQuestionKeywords.some(keyword => lowerMessage.includes(keyword))) {
      return true;
    }

    // Si le message est long (probablement une question complexe)
    if (message.length > 50) {
      return true;
    }

    // Si le message contient un point d'interrogation et n'est pas une requête simple
    if (message.includes('?') && !this.isSimpleRequest(message)) {
      return true;
    }

    return false;
  }

  // Vérifier si c'est une requête simple (pas besoin d'IA)
  private isSimpleRequest(message: string): boolean {
    const lowerMessage = message.toLowerCase();
    const simpleRequests = [
      'horaires', 'prière', 'prayer', 'verset', 'verse', 'hadith', 'doua', 'dua',
      'nom d\'allah', 'asma', 'random', 'aléatoire'
    ];

    return simpleRequests.some(request => lowerMessage.includes(request));
  }
}

export const aiService = new AIService();

